// Identidad del colaborador para encabezados de pantalla (breadcrumbs).
//
// El formato acordado es "ApellidoPaterno ApellidoMaterno Nombres - RUT", con el
// RUT sin separadores de miles. Vive centralizado aquí porque el mismo texto se
// usa en el perfil, en el listado de contratos y en el detalle del contrato, y
// antes estaba duplicado inline en cada pantalla con órdenes y formatos distintos.
//
// Nota sobre el RUT: UserExtraData.RutCompleto ya viene como "12345678-9" y las
// queries lo entregan sin transformar, así que formatRutWithoutDots aquí actúa
// solo como normalizador de los casos de borde que existen en los datos (DV en
// minúscula, y unos pocos registros con puntos).

import { formatPersonName, toCapitalCase } from './utils';
import { formatRutWithoutDots } from './rut';


export interface EmployeeNameParts {
  firstNames?: string | null;
  lastName?: string | null;
  secondLastName?: string | null;
}

/**
 * Arma el nombre completo del colaborador desde sus campos sueltos, en el orden
 * de presentación "ApellidoPaterno ApellidoMaterno Nombres".
 *
 * Se usa cuando la pantalla tiene el perfil completo. Cuando el backend ya
 * entrega el nombre armado (lookups, listados) hay que pasarlo directo a
 * formatEmployeeIdentity, que respeta el orden que venga.
 *
 * @returns Nombre normalizado, o '' si no hay ningún dato disponible.
 */
export const buildEmployeeFullName = ({
  firstNames,
  lastName,
  secondLastName,
}: EmployeeNameParts): string =>
  toCapitalCase([lastName, secondLastName, firstNames].filter(Boolean).join(' '));

/**
 * Combina nombre y RUT en la identidad que se muestra en el breadcrumb.
 *
 * @param fullName Nombre completo, ya en su orden de presentación.
 * @param rut RUT del colaborador, con o sin formato.
 * @returns "Garcia Díaz Diana Sofía - 12345678-9", solo el nombre si no hay RUT,
 *          o '' si no hay nombre.
 */
export const formatEmployeeIdentity = (
  fullName: string | null | undefined,
  rut?: string | null,
): string => {
  const name = formatPersonName(fullName || '');
  if (!name) return '';

  const formattedRut = formatRutWithoutDots(rut || '');
  return formattedRut ? `${name} - ${formattedRut}` : name;
};
